import {
    ProductProvider,
    receivedRequestProvider as defaultReceivedRequestProvider,
} from '@core/domain/product'
import { DocumentResearchApi, ProductApi, SubstanceApi } from '@core/api'
import {
    buildSubstanceListVisibilityProductContext,
} from '@core/domain/substance/SubstanceListVisibilityContext'
import { InformationLevel } from '@core/domain/legislation'
import {
    ConcernElementsInformationOwner,
    DocumentType,
    getNameOrId,
    ProductIdentifiers,
    ProductNames,
    ProductType,
    RequestStatus,
    setNameOrId,
} from '@/libs/product'
import { createComplexComplianceCompositionStore } from './complex-compliance-composition-store'

const COMPLEX_COMPLIANCE_INCLUDES = [
    'substance-list-question-states',
    'excluded-substances',
    'component-identifiers',
    'safety-instructions',
]

const isRequestId = requestId => requestId !== null && typeof requestId !== 'undefined' && requestId !== ''
const RECEIVED_REQUEST_WORKFLOW_MODES = Object.freeze(['single', 'bulk'])
const normalizeRequestIds = requestIds => Array.from(new Set(
    (Array.isArray(requestIds) ? requestIds : [])
        .map(requestId => Number(requestId))
        .filter(requestId => Number.isInteger(requestId) && requestId > 0),
))
const normalizeScipNumber = value => (
    value === null || typeof value === 'undefined' ? '' : String(value).trim()
)

const createReceivedRequestWorkflowContext = async ({
    requestId,
    mode = 'single',
    additionalRequestIds = [],
    updateAdditionalRequestIds = null,
    receivedRequestProvider = defaultReceivedRequestProvider,
    forwardRequest = null,
    openSentRequest = null,
    declarationApprovalProvider = null,
}) => {
    if (!isRequestId(requestId) || typeof receivedRequestProvider !== 'function') {
        throw new Error('Received request workflow context requires requestId and receivedRequestProvider()')
    }

    if (!RECEIVED_REQUEST_WORKFLOW_MODES.includes(mode)) {
        throw new Error(`Received request workflow context does not support mode "${mode}"`)
    }

    const requestedAdditionalRequestIds = normalizeRequestIds(additionalRequestIds)
        .filter(additionalRequestId => String(additionalRequestId) !== String(requestId))

    if (mode === 'bulk' && requestedAdditionalRequestIds.length === 0) {
        throw new Error('Received request bulk workflow requires at least one additional request id')
    }

    const requestProvider = receivedRequestProvider(requestId)

    if (!requestProvider || typeof requestProvider.getRequest !== 'function' || typeof requestProvider.getProduct !== 'function') {
        throw new Error('Received request workflow context requires a request provider with getRequest() and getProduct()')
    }

    await requestProvider.getRequest()

    const { request } = requestProvider

    if (!request || !request.product || !request.product.id) {
        throw new Error('Received request workflow context requires a loaded request product')
    }

    const productProvider = await requestProvider.getProduct()

    if (!productProvider || typeof productProvider.getSubstances !== 'function') {
        throw new Error('Received request workflow context requires a product provider with getSubstances()')
    }

    const declarationApproval = declarationApprovalProvider
        && typeof declarationApprovalProvider.getState === 'function'
        ? await declarationApprovalProvider.getState({ requestId })
        : { required: false, approved: false, scopeId: null }

    const context = {
        mode,
        requestId,
        productId: request.product.id,
        request,
        product: request.product,
        productAttributes: null,
        productHierarchy: { children: [] },
        productWeightRequired: false,
        componentConcernSubstances: [],
        includedSubstances: [],
        excludedSubstances: [],
        declarationApproval: {
            required: declarationApproval.required === true,
            approved: declarationApproval.approved === true,
            scopeId: declarationApproval.scopeId || null,
            identity: {
                name: declarationApproval.identity && declarationApproval.identity.name
                    ? declarationApproval.identity.name
                    : '',
                email: declarationApproval.identity && declarationApproval.identity.email
                    ? declarationApproval.identity.email
                    : '',
            },
        },
        bulkReplyCandidates: {
            candidates: [],
            error: null,
            loaded: false,
            requestedRequestIds: mode === 'bulk' ? requestedAdditionalRequestIds : [],
            selectedRequestIds: mode === 'bulk' ? requestedAdditionalRequestIds : [],
        },
        documentsAndWebpages: {
            completedUploadRevision: null,
            completedLinkRevision: null,
            completedProcessingRevision: 0,
            documents: [],
            links: [],
            researchProcesses: [],
            researchLoaded: false,
            reviewRefreshPendingProcessIds: [],
            loaded: false,
            linksLoaded: false,
        },
        replyDocuments: {
            verificationDocuments: [],
            loaded: false,
        },
        requestHandling: {
            workflowState: {
                schemaVersion: null,
                strategy: null,
                loaded: false,
                saving: false,
                error: null,
            },
            forwardedRequests: {
                items: [],
                loaded: false,
                loading: false,
                error: null,
            },
            forwardModal: {
                targets: [],
                loaded: false,
                loading: false,
                submitting: false,
                error: null,
            },
            productStructure: {
                loaded: false,
                loading: false,
                busyNodeKey: null,
            },
        },
        replySubmission: {
            authorizedToSubmit: false,
            allowInformationReuse: false,
            refreshError: null,
        },
        complianceInformation: {
            appliedDocumentProcessingRevision: 0,
            substanceLists: [],
            visibilityContext: {},
            propertyConfig: {
                map: { list: {}, regulation: {} },
                properties: {},
            },
            safetyInstructions: {
                loaded: false,
                isSafeUseApplicable: null,
                safeUseDescription: '',
                disassemblingDescription: '',
            },
            loaded: false,
        },
        permissions: {},
        services: null,
    }
    const complexComplianceComposition = createComplexComplianceCompositionStore()

    const isComplexRequestProduct = () => (
        request.isComplexProduct === true
        || (request.product && request.product.isComplex === true)
    )

    const complianceListsByIds = listIds => {
        const ids = (listIds || []).map(id => String(id))
        return context.complianceInformation.substanceLists
            .filter(list => ids.includes(String(list.id)))
            .map(list => (typeof list.toDTO === 'function' ? list.toDTO() : list))
    }

    const calculateInformationOwner = async (save = true) => {
        const { informationOwner } = request

        if (
            (informationOwner === null || informationOwner === ConcernElementsInformationOwner.WE_PARTIALLY)
            && request.product.type === ProductType.OWN
        ) {
            requestProvider.setInformationOwner(ConcernElementsInformationOwner.WE)
            if (save) {
                await requestProvider.save()
            }
            return
        }

        if (
            informationOwner === ConcernElementsInformationOwner.WE
            && [ProductType.PURCHASED, ProductType.PURCHASED_UNDEFINED_SUPPLIER]
                .includes(request.product.type)
        ) {
            const nextOwner = context.includedSubstances.length > 0 && request.product.hasConcernSubstances
                ? ConcernElementsInformationOwner.WE_PARTIALLY
                : null

            requestProvider.setInformationOwner(nextOwner)
            if (save) {
                await requestProvider.save()
            }
        }
    }

    const refreshProductWeightRequirement = async () => {
        const [{ default: LegislationApi }, { getSubstanceList }] = await Promise.all([
            import('@core/api/request/legislation'),
            import('@core/domain/substance'),
        ])
        const { data: legislations } = await LegislationApi.all()
        const requestedIds = (request.legislations || []).map(legislation => legislation.id)
        const relevantLegislations = legislations.filter(legislation => (
            requestedIds.includes(legislation.id)
            && legislation.requirements.requiresProductWeight
        ))
        const canRequireWeight = relevantLegislations.length > 0
            || request.informationLevel === InformationLevel.FULL_MATERIAL_DECLARATION

        if (!canRequireWeight || relevantLegislations.length === 0) {
            context.productWeightRequired = false
            return false
        }

        let substances
        if (!isComplexRequestProduct()) {
            const productSubstances = await productProvider.getSubstances()
            substances = productSubstances.included.map(container => container.substance)
        } else {
            const substancesById = {}

            context.componentConcernSubstances.forEach(component => {
                component.substances.forEach(container => {
                    substancesById[container.substance.id] = container.substance
                })
            })
            substances = Object.values(substancesById)
        }

        for (let index = 0; index < relevantLegislations.length; index += 1) {
            const legislation = relevantLegislations[index]
            // The list checks are intentionally sequential to stop after the first match.
            // eslint-disable-next-line no-await-in-loop
            const substanceList = await getSubstanceList(legislation.substanceList.listKey)
            // eslint-disable-next-line no-await-in-loop
            const matchingSubstances = await substanceList.extractIncluded(substances)

            if (matchingSubstances.length > 0) {
                context.productWeightRequired = true
                return true
            }
        }

        context.productWeightRequired = false
        return false
    }

    const refreshProductStructureHierarchy = async () => {
        productProvider.hierarchyChanged()
        const hierarchy = await productProvider.getHierarchy()

        Object.assign(context.productHierarchy, hierarchy || { children: [] })
        context.requestHandling.productStructure.loaded = true

        return context.productHierarchy
    }

    const createProductStructureComponent = async details => {
        let componentId = details.existingProductId

        if (!componentId) {
            const suppliers = details.suppliers || []
            const purchased = details.productType === ProductType.PURCHASED
            const names = [{
                key: ProductNames.OUR_PRODUCT_NAME,
                value: details.name,
            }]
            const identifiers = []
            const type = purchased
                ? (suppliers.length > 0
                    ? ProductType.PURCHASED
                    : ProductType.PURCHASED_UNDEFINED_SUPPLIER)
                : ProductType.OWN

            if (purchased) {
                setNameOrId(
                    names,
                    ProductNames.SUPPLIERS_PRODUCT_NAME,
                    details.supplierProductName,
                )
                if (details.supplierProductIdentifier) {
                    setNameOrId(
                        identifiers,
                        ProductIdentifiers.SUPPLIERS_PRODUCT_ID,
                        details.supplierProductIdentifier,
                    )
                }
            }

            const { data: product } = await ProductApi.add({
                type,
                names,
                identifiers,
                suppliers: suppliers.map(supplier => supplier.id),
            })
            componentId = product.id
        }

        const normalizedComponentId = Number(componentId)

        if (!Number.isInteger(normalizedComponentId) || normalizedComponentId <= 0) {
            throw new Error('Product structure component id must be a positive integer')
        }

        await ProductApi.includeComponents(details.parentId, [{
            id: normalizedComponentId,
            numberOfUnits: details.numberOfUnits,
        }])

        return normalizedComponentId
    }

    const updateProductStructureComponent = async details => {
        await ProductApi.updateComponent(details.parentId, {
            id: details.componentId,
            numberOfUnits: details.numberOfUnits,
        })

        if (
            details.productType !== ProductType.PURCHASED
            && details.productType !== ProductType.PURCHASED_UNDEFINED_SUPPLIER
        ) {
            return
        }

        const componentProvider = ProductProvider(details.componentId)
        const card = await componentProvider.getCard()
        const suppliers = details.suppliers || []

        card.names = card.names || []
        card.identifiers = card.identifiers || []
        card.suppliers = card.suppliers || []

        setNameOrId(
            card.names,
            ProductNames.SUPPLIERS_PRODUCT_NAME,
            details.supplierProductName,
        )
        setNameOrId(
            card.identifiers,
            ProductIdentifiers.SUPPLIERS_PRODUCT_ID,
            details.supplierProductIdentifier,
        )
        card.suppliers.splice(0, card.suppliers.length, ...suppliers)
        await componentProvider.saveCard()

        const targetType = suppliers.length > 0
            ? ProductType.PURCHASED
            : ProductType.PURCHASED_UNDEFINED_SUPPLIER

        if (card.type !== targetType) {
            await componentProvider.convertType(targetType)
        }
    }

    const refreshDeclarationApproval = async () => {
        if (!declarationApprovalProvider || typeof declarationApprovalProvider.getState !== 'function') {
            return context.declarationApproval
        }

        const nextState = await declarationApprovalProvider.getState({ requestId })
        Object.assign(context.declarationApproval, {
            required: nextState.required === true,
            approved: nextState.approved === true,
            scopeId: nextState.scopeId || null,
        })

        return context.declarationApproval
    }

    const refreshBulkReplyCandidates = async ({ force = false } = {}) => {
        if (context.bulkReplyCandidates.loaded && !force) {
            return context.bulkReplyCandidates
        }

        try {
            const { data } = await requestProvider.getBulkReplyCandidates()
            const candidates = Array.isArray(data.candidates) ? data.candidates : []

            Object.assign(context.bulkReplyCandidates, {
                candidates,
                error: null,
                loaded: true,
                selectedRequestIds: context.bulkReplyCandidates.selectedRequestIds
                    .filter(id => candidates.some(candidate => candidate.requestId === id)),
            })
        } catch (error) {
            Object.assign(context.bulkReplyCandidates, {
                candidates: [],
                error,
                loaded: false,
                selectedRequestIds: [],
            })
        }

        return context.bulkReplyCandidates
    }

    let backgroundInitializationPromise = null
    const initializeBackgroundData = () => {
        if (backgroundInitializationPromise) {
            return backgroundInitializationPromise
        }

        backgroundInitializationPromise = productProvider.getSubstances()
            .then(({ included = [], excluded = [] }) => {
                context.includedSubstances.splice(
                    0,
                    context.includedSubstances.length,
                    ...included,
                )
                context.excludedSubstances.splice(
                    0,
                    context.excludedSubstances.length,
                    ...excluded,
                )
            })

        return backgroundInitializationPromise
    }

    const createReplyDeclaration = async () => {
        const { data: documents = [] } = await ProductApi.getReceivedRequestDocuments(
            requestId,
            DocumentType.VERIFICATION,
        )
        const otherInformation = request.otherInformation || {}

        return {
            documents,
            internalComment: otherInformation.internalComment || null,
            declarationValidity: otherInformation.declarationValidity || null,
            customerInformation: otherInformation.customerInformation || null,
            authorizedToSubmit: context.replySubmission.authorizedToSubmit === true,
            allowInformationReuse: context.replySubmission.allowInformationReuse === true,
        }
    }

    context.services = Object.freeze({
        saveProductCard: () => productProvider.saveCard(),
        getProductSafetyInstructions: async () => {
            const documents = await productProvider.getDocuments()

            return {
                isSafeUseApplicable: productProvider.isSafeUseApplicable,
                safeUseDescription: documents.safeUseDocument.description || '',
                disassemblingDescription: documents.dissemblingDocument.description || '',
            }
        },
        setProductSafeUseApplicable: value => {
            productProvider.isSafeUseApplicable = value
            context.product.isSafeUseApplicable = value
        },
        setProductSafeUseInstructions: description => {
            productProvider.setSafeUseDocument({ description })
        },
        setProductDisassemblingInstructions: description => {
            productProvider.setDissemblingDocument({ description })
        },
        saveProductSafetyInstructions: async () => {
            await productProvider.saveVerificationDocuments()
            await productProvider.getDocuments()
        },
        changeScipNumber: value => {
            setNameOrId(
                request.product.identifiers,
                ProductIdentifiers.SCIP_NUMBER,
                value,
            )
        },
        getProductAttributes: async () => {
            const { default: productApi } = await import('@core/api/request/product')
            const { data } = await productApi.getAttributes()

            return data
        },
        getProductHierarchy: () => productProvider.getHierarchy(),
        refreshProductStructureHierarchy,
        searchProductStructureComponents: async query => {
            const { data } = await ProductApi.searchIncludedProducts(query)
            return Array.isArray(data) ? data : []
        },
        searchReviewProducts: async ({ mode, supplierId = null, query = '' }) => {
            if (mode === 'organization-products') {
                const { data } = await ProductApi.searchProducts(query)
                return data && Array.isArray(data.items) ? data.items : []
            }

            if (mode === 'supplier-products' && supplierId) {
                const { data } = await ProductApi.searchPurchasedProducts(supplierId, query)
                return Array.isArray(data) ? data : []
            }

            return []
        },
        getProductStructureComponent: async id => ProductProvider(id).getCard(),
        createProductStructureComponent,
        updateProductStructureComponent,
        removeProductStructureComponent: ({ parentId, componentId }) => (
            ProductApi.excludeComponent(parentId, componentId)
        ),
        getReceivedRequestWorkflowState: () => ProductApi.getReceivedRequestWorkflowState(requestId),
        saveReceivedRequestWorkflowState: workflowState => (
            ProductApi.updateReceivedRequestWorkflowState(requestId, workflowState)
        ),
        changeRequestHandlingStrategy: async ({ informationOwner, isComplexProduct }) => {
            requestProvider.setInformationOwner(informationOwner)
            requestProvider.setIsComplex(isComplexProduct)
            await requestProvider.save()
        },
        changeProductType: async type => {
            await productProvider.convertType(type)
            await calculateInformationOwner()
        },
        convertProductType: type => productProvider.convertType(type),
        changeProductSuppliers: suppliers => productProvider.changeSuppliers(suppliers),
        saveProductSupplierDetails: ({ name = '', identifier = '', suppliers = [] }) => {
            setNameOrId(request.product.names, ProductNames.SUPPLIERS_PRODUCT_NAME, name)
            setNameOrId(
                request.product.identifiers,
                ProductIdentifiers.SUPPLIERS_PRODUCT_ID,
                identifier,
            )
            request.product.suppliers.splice(
                0,
                request.product.suppliers.length,
                ...suppliers,
            )

            return productProvider.saveCard()
        },
        uploadReceivedRequestUnclassifiedVerificationDocuments: files => (
            ProductApi.uploadReceivedRequestUnclassifiedVerificationDocuments(requestId, files)
        ),
        getReceivedRequestUnclassifiedVerificationDocuments: () => (
            ProductApi.getReceivedRequestUnclassifiedVerificationDocuments(requestId)
        ),
        getReceivedRequestVerificationDocuments: () => (
            ProductApi.getReceivedRequestDocuments(requestId, DocumentType.VERIFICATION)
        ),
        getReceivedRequestDocumentationLinks: () => (
            ProductApi.getReceivedRequestDocumentationLinks(requestId)
        ),
        addReceivedRequestDocumentationLinks: urls => (
            ProductApi.addReceivedRequestDocumentationLinks(requestId, urls)
        ),
        getDocumentResearchStatuses: processIds => (
            DocumentResearchApi.getStatuses(processIds)
        ),
        getDocumentResearchProcess: processId => (
            DocumentResearchApi.getProcess(processId)
        ),
        getDocumentResearchCandidateContent: ({ processId, candidateId }) => (
            DocumentResearchApi.getCandidateContent(processId, candidateId)
        ),
        selectDocumentResearchDocuments: ({ processId, candidateIds }) => (
            DocumentResearchApi.selectDocuments(processId, candidateIds)
        ),
        finishDocumentResearchWithoutDocuments: processId => (
            DocumentResearchApi.finishWithoutDocuments(processId)
        ),
        getReceivedRequestForwardedRequests: () => (
            ProductApi.getReceivedRequestForwardedRequests(requestId)
        ),
        getForwardRequestTargets: () => (
            ProductApi.getComponentsToRequest(request.product.id)
        ),
        saveForwardRequestTargetIdentity: async ({ id, name = '', identifier = '' }) => {
            const targetId = Number(id)

            if (!Number.isInteger(targetId) || targetId <= 0) {
                throw new Error('Forward request target id must be a positive integer')
            }

            const targetProvider = ProductProvider(targetId)
            const card = await targetProvider.getCard()

            card.names = card.names || []
            card.identifiers = card.identifiers || []
            setNameOrId(card.names, ProductNames.SUPPLIERS_PRODUCT_NAME, name)
            setNameOrId(
                card.identifiers,
                ProductIdentifiers.SUPPLIERS_PRODUCT_ID,
                identifier,
            )

            return targetProvider.saveCard()
        },
        submitReceivedRequestForward: async payload => {
            const response = await ProductApi.forwardRequest(requestId, payload)
            request.status = RequestStatus.FORWARDED_TO_SUPPLIER

            return response
        },
        changeProductComplexity: async isComplex => {
            requestProvider.setIsComplex(isComplex)
            await calculateInformationOwner(false)
            await requestProvider.save()
        },
        setProductCharacteristics: characteristics => productProvider.setProductCharacteristics(characteristics),
        refreshProductWeightRequirement,
        getComponentConcernSubstances: async () => {
            if (!isComplexRequestProduct()) {
                return productProvider.getComponentsSubstancesForLegislations(request.legislations)
            }

            const { data = [] } = await ProductApi.complexProductComposition(
                request.product.id,
                COMPLEX_COMPLIANCE_INCLUDES,
            )
            return complexComplianceComposition.replace(data)
        },
        getComplianceComponentEditorData: componentId => (
            Promise.resolve(complexComplianceComposition.getEditorData(componentId))
        ),
        includeComplianceComponentSubstance: (componentId, substance, listIds, draft = false) => (
            complexComplianceComposition.includeSubstance(
                componentId,
                substance,
                complianceListsByIds(listIds),
                draft,
            )
        ),
        excludeComplianceComponentSubstance: (componentId, substance, draft = false) => (
            complexComplianceComposition.excludeSubstance(componentId, substance, draft)
        ),
        makeComplianceComponentSubstanceUndefined: (componentId, substance, draft = false) => (
            complexComplianceComposition.makeSubstanceUndefined(componentId, substance, draft)
        ),
        updateComplianceComponentListQuestion: async (componentId, list, state) => {
            const response = await ProductApi.updateSubstanceListQuestionState(
                componentId,
                list.id,
                state,
            )
            complexComplianceComposition.updateQuestionState(componentId, list.id, state)
            return response
        },
        updateComplianceComponentSubstanceProperties: (componentId, substance, properties) => (
            complexComplianceComposition.updateSubstanceProperties(
                componentId,
                substance,
                properties,
            )
        ),
        getComplianceComponentSafetyInstructions: componentId => (
            complexComplianceComposition.getSafetyInstructions(componentId)
        ),
        updateComplianceComponentSafetyInstructions: (componentId, changes) => (
            complexComplianceComposition.updateSafetyInstructions(componentId, changes)
        ),
        saveComplianceComponentSafetyInstructions: componentId => {
            const instructions = complexComplianceComposition.getSafetyInstructions(componentId)

            return ProductApi.saveOrganizationDocuments(
                componentId,
                [],
                [],
                [],
                {
                    id: instructions.safeUseDocumentId,
                    description: instructions.safeUseDescription,
                },
                {
                    id: instructions.disassemblingDocumentId,
                    description: instructions.disassemblingDescription,
                },
                instructions.isSafeUseApplicable,
                null,
            )
        },
        saveComplianceComponentSubstances: (componentId, draft = false) => {
            const { included, excluded } = complexComplianceComposition.savePayload(componentId, draft)
            return ProductApi.saveSubstances(componentId, included, excluded)
        },
        setComplianceComponentScipNumber: (componentId, value) => {
            const normalizedValue = normalizeScipNumber(value)
            const identifiers = normalizedValue
                ? [{ key: ProductIdentifiers.SCIP_NUMBER, value: normalizedValue }]
                : []
            complexComplianceComposition.replaceIdentifierGroup(
                componentId,
                ProductIdentifiers.SCIP_NUMBER,
                identifiers,
            )
        },
        saveComplianceComponentScipNumber: (componentId, value) => {
            const normalizedValue = normalizeScipNumber(value)
            return normalizedValue
                ? ProductApi.patchIdentifiers(
                    componentId,
                    [{ key: ProductIdentifiers.SCIP_NUMBER, value: normalizedValue }],
                    [],
                )
                : ProductApi.patchIdentifiers(
                    componentId,
                    [],
                    [ProductIdentifiers.SCIP_NUMBER],
                )
        },
        refreshConcernSubstances: () => productProvider.getSubstances(true),
        refreshComplianceProductCard: async () => {
            const freshCard = await productProvider.refreshCard()

            Object.assign(context.product, freshCard)
            productProvider.setCard(context.product)

            return context.product
        },
        refreshSubstanceState: () => requestProvider.refreshSubstanceState(),
        getComplianceSubstanceLists: async () => {
            const { getAllSubstanceLists } = await import('@core/domain/substance')
            return getAllSubstanceLists()
        },
        getComplianceSubstancePropertyConfig: async () => {
            const { default: SubstanceApi } = await import('@core/api/request/substance')
            const { data } = await SubstanceApi.getProductPropertyData()
            return data
        },
        getComplianceSubstanceListVisibilityContext: async () => {
            const [materialCategoriesResponse, mixtureCategoriesResponse] = await Promise.all([
                SubstanceApi.materialCategories(),
                ProductApi.mixtureCategories(),
            ])

            return buildSubstanceListVisibilityProductContext(
                context.product,
                materialCategoriesResponse.data || [],
                mixtureCategoriesResponse.data || [],
            )
        },
        forwardRequest: () => {
            if (typeof forwardRequest !== 'function') {
                throw new Error('Received request workflow context requires forwardRequest() for forwarding')
            }

            if (request.informationOwner === ConcernElementsInformationOwner.SUPPLIER) {
                const hasSupplier = Array.isArray(request.product.suppliers)
                    && request.product.suppliers.length > 0
                const supplierProductName = getNameOrId(
                    request.product.names || [],
                    ProductNames.SUPPLIERS_PRODUCT_NAME,
                )

                if (
                    request.product.type !== ProductType.PURCHASED
                    || !hasSupplier
                    || !supplierProductName
                ) {
                    throw new Error(
                        'Select a supplier and save its product name before forwarding the request.',
                    )
                }
            }

            return forwardRequest(request)
        },
        openSentRequest: sentRequest => {
            if (typeof openSentRequest !== 'function') {
                throw new Error(
                    'Received request workflow context requires openSentRequest() for sent request navigation',
                )
            }

            return openSentRequest(sentRequest)
        },
        changeInformationOwner: async informationOwner => {
            requestProvider.setInformationOwner(informationOwner)
            await requestProvider.save()
        },
        setHasConcernSubstances: value => {
            request.product.hasConcernSubstances = value
            productProvider.setHasConcernSubstances(value)
        },
        updateConcernSubstanceProperties: (substance, properties) => {
            Object.assign(substance, { properties })
        },
        includeConcernSubstance: (substance, listIds) => productProvider.includeSubstance(substance, listIds),
        excludeConcernSubstance: substance => productProvider.excludeSubstance(substance),
        makeConcernSubstanceUndefined: substance => productProvider.makeSubstanceUndefined(substance),
        updateListQuestionState: (list, state) => productProvider.updateListQuestionState(list, state),
        updateInformationOwnerSettings: settings => productProvider.setInformationOwnerSettings(settings),
        saveSubstances: () => productProvider.saveSubstances(),
        changeOtherInformation: otherInformation => requestProvider.setOtherInformation(otherInformation),
        saveReceivedRequest: () => requestProvider.save(),
        refreshWorkflowAvailability: refreshDeclarationApproval,
        refreshReceivedRequest: async () => {
            const response = await ProductApi.getCustomerRequest(requestId)
            Object.assign(request, response.data || {})

            return request
        },
        refreshBulkReplyCandidates,
        updateBulkReplySelection: async requestIds => {
            const candidateIds = context.bulkReplyCandidates.candidates
                .map(candidate => candidate.requestId)
            const selectedRequestIds = normalizeRequestIds(requestIds)
                .filter(id => candidateIds.includes(id))

            context.bulkReplyCandidates.selectedRequestIds.splice(
                0,
                context.bulkReplyCandidates.selectedRequestIds.length,
                ...selectedRequestIds,
            )

            if (typeof updateAdditionalRequestIds === 'function') {
                await updateAdditionalRequestIds(selectedRequestIds)
            }

            return context.bulkReplyCandidates.selectedRequestIds
        },
        initializeBackgroundData,
        sendReceivedRequestReply: async () => {
            const declaration = await createReplyDeclaration()
            const selectedRequestIds = context.bulkReplyCandidates.selectedRequestIds
            const response = selectedRequestIds.length > 0
                ? await ProductApi.replyToRequestBulk(requestId, declaration, selectedRequestIds)
                : await ProductApi.replyToRequest(requestId, declaration)

            request.status = RequestStatus.REPLIED
            context.replySubmission.refreshError = null

            try {
                await context.services.refreshReceivedRequest()
            } catch (error) {
                context.replySubmission.refreshError = error
            }

            return response
        },
        refreshDeclarationApproval,
        requestDeclarationApprovalCode: ({ name, email }) => (
            ProductApi.sendApproveDeclarationMessage(
                'email',
                requestId,
                name,
                email,
            )
        ),
        verifyDeclarationApprovalCode: async code => {
            await ProductApi.approveDeclaration(requestId, code)

            if (!declarationApprovalProvider || typeof declarationApprovalProvider.markApproved !== 'function') {
                throw new Error('Received request workflow context requires declarationApprovalProvider.markApproved()')
            }

            await declarationApprovalProvider.markApproved({
                requestId,
                scopeId: context.declarationApproval.scopeId,
            })

            return refreshDeclarationApproval()
        },
        markDeclarationApproved: async () => {
            if (!declarationApprovalProvider || typeof declarationApprovalProvider.markApproved !== 'function') {
                throw new Error('Received request workflow context requires declarationApprovalProvider.markApproved()')
            }

            await declarationApprovalProvider.markApproved({
                requestId,
                scopeId: context.declarationApproval.scopeId,
            })

            return refreshDeclarationApproval()
        },
    })

    if (mode === 'bulk') {
        const bulkReplyCandidates = await refreshBulkReplyCandidates()

        if (bulkReplyCandidates.error) {
            throw bulkReplyCandidates.error
        }

        if (bulkReplyCandidates.selectedRequestIds.length === 0) {
            throw new Error('No selected requests are eligible for this mass reply')
        }
    }

    return context
}

export { createReceivedRequestWorkflowContext }
export default createReceivedRequestWorkflowContext
